import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { getLocalTimeZone, today } from "@internationalized/date";
import type { Selection, SortDescriptor } from "@heroui/react";
import type { DateRangeValue, Order, FilterListItem, StatusOption } from "@/components/orderList/types";

const INITIAL_VISIBLE_COLUMNS = [
  "subject",
  "status",
  "customer_name",
  "order_delivery_date",
  "reception_date",
  "assignment",
  "warnings",
  "products_counter",
  "actions",
];

const INITIAL_SORT_DESCRIPTOR: SortDescriptor = {
  column: "deadline",
  direction: "ascending",
};

const DEFAULT_STATUS_FILTER = ["pending_review", "archive", "success", "failed"];

interface UseOrdersFiltersProps {
  orders: Order[];
  statusOptions: StatusOption[];
  configuredDays: number;
  isSettingsLoaded: boolean;
}

interface UseOrdersFiltersResult {
  // Search
  filterValue: string;
  setFilterValue: (value: string) => void;
  onSearchChange: (value: string) => void;
  hasSearchFilter: boolean;
  // Status filter
  statusFilter: Selection;
  setStatusFilter: (value: Selection) => void;
  // Client filter
  clientList: FilterListItem[];
  clientListFilter: string | null;
  setClientListFilter: (value: string | null) => void;
  // Entity filter
  entityList: FilterListItem[];
  entityListFilter: Selection;
  setEntityListFilter: (value: Selection) => void;
  // Assignment filter
  assignmentFilter: Selection;
  setAssignmentFilter: (value: Selection) => void;
  // Period filter
  periodFilterInterval: DateRangeValue | null;
  setPeriodFilterInterval: (value: DateRangeValue | null) => void;
  handleRadioChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  // Columns
  visibleColumns: Selection;
  setVisibleColumns: (value: Selection) => void;
  // Pagination
  rowsPerPage: number;
  setRowsPerPage: (value: number) => void;
  onRowsPerPageChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  page: number;
  setPage: (value: number) => void;
  // Sort
  sortDescriptor: SortDescriptor;
  setSortDescriptor: (value: SortDescriptor) => void;
  // Filtered items
  filteredItems: Order[];
}

export const useOrdersFilters = ({
  orders,
  statusOptions,
  configuredDays,
  isSettingsLoaded,
}: UseOrdersFiltersProps): UseOrdersFiltersResult => {
  // Period filter initialization
  const [periodFilterInterval, setPeriodFilterInterval] = useState<DateRangeValue | null>(null);
  const hasInitializedPeriodFilter = useRef(false);

  useEffect(() => {
    if (isSettingsLoaded && !hasInitializedPeriodFilter.current) {
      hasInitializedPeriodFilter.current = true;
      setPeriodFilterInterval({
        start: today(getLocalTimeZone()).subtract({ days: configuredDays }),
        end: today(getLocalTimeZone()),
      });
    }
  }, [isSettingsLoaded, configuredDays]);

  // Search filter
  const [filterValue, setFilterValue] = useState("");
  const hasSearchFilter = Boolean(filterValue);

  // Status filter with localStorage persistence
  const [statusFilter, setStatusFilter] = useState<Selection>(
    () =>
      new Set(
        JSON.parse(localStorage.getItem("statusFilter") || JSON.stringify(DEFAULT_STATUS_FILTER))
      )
  );

  // Client, entity and assignment filters
  const [clientListFilter, setClientListFilter] = useState<string | null>(null);
  const [entityListFilter, setEntityListFilter] = useState<Selection>(() => {
    const saved = localStorage.getItem("entityListFilter");
    return saved ? new Set(JSON.parse(saved)) : "all";
  });
  const [assignmentFilter, setAssignmentFilter] = useState<Selection>(() => {
    const saved = localStorage.getItem("assignmentFilter");
    return saved ? new Set(JSON.parse(saved)) : "all";
  });

  // Visible columns with localStorage persistence
  const [visibleColumns, setVisibleColumns] = useState<Selection>(() => {
    const savedColumns = localStorage.getItem("visibleColumns");
    return savedColumns
      ? new Set(JSON.parse(savedColumns))
      : new Set(INITIAL_VISIBLE_COLUMNS);
  });

  // Rows per page with localStorage persistence
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const savedRowsPerPage = localStorage.getItem("rowsPerPage");
    return savedRowsPerPage ? Number(savedRowsPerPage) : 15;
  });

  // Sort descriptor with localStorage persistence
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>(() => {
    const savedSort = localStorage.getItem("sortDescriptor");
    return savedSort ? JSON.parse(savedSort) : INITIAL_SORT_DESCRIPTOR;
  });

  // Pagination
  const [page, setPage] = useState(1);

  // Build client and entity lists from orders
  const clientList = useMemo(() => {
    return Array.from(new Set(orders.map((order) => order.customer_name || "N/A"))).map(
      (name) => ({ name, uid: name })
    );
  }, [orders]);

  const entityList = useMemo(() => {
    return Array.from(new Set(orders.map((order) => order.entity_name || "N/A"))).map(
      (name) => ({ name, uid: name })
    );
  }, [orders]);

  // localStorage persistence effects
  useEffect(() => {
    localStorage.setItem("visibleColumns", JSON.stringify(Array.from(visibleColumns)));
  }, [visibleColumns]);

  useEffect(() => {
    localStorage.setItem("sortDescriptor", JSON.stringify(sortDescriptor));
  }, [sortDescriptor]);

  useEffect(() => {
    localStorage.setItem("statusFilter", JSON.stringify(Array.from(statusFilter)));
  }, [statusFilter]);

  useEffect(() => {
    if (assignmentFilter === "all") {
      localStorage.removeItem("assignmentFilter");
    } else {
      localStorage.setItem("assignmentFilter", JSON.stringify(Array.from(assignmentFilter)));
    }
  }, [assignmentFilter]);

  useEffect(() => {
    if (entityListFilter === "all") {
      localStorage.removeItem("entityListFilter");
    } else {
      localStorage.setItem("entityListFilter", JSON.stringify(Array.from(entityListFilter)));
    }
  }, [entityListFilter]);

  useEffect(() => {
    localStorage.setItem("rowsPerPage", String(rowsPerPage));
  }, [rowsPerPage]);

  // Callbacks
  const onSearchChange = useCallback((value: string) => {
    if (value) {
      setFilterValue(value);
      setPage(1);
    } else {
      setFilterValue("");
    }
  }, []);

  const onRowsPerPageChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
  }, []);

  const handleRadioChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;
    if (!value) {
      value = event.target.getAttribute("value") || "3";
    }
    const days = Number.parseInt(value, 10);
    setPeriodFilterInterval({
      start: today(getLocalTimeZone()).subtract({ days }),
      end: today(getLocalTimeZone()),
    });
  }, []);

  // Filter orders
  const filteredItems = useMemo(() => {
    let filtered = [...orders];

    if (hasSearchFilter) {
      filtered = filtered.filter(
        (order) =>
          order.subject?.toLowerCase().includes(filterValue.toLowerCase()) ||
          order.jobID?.toLowerCase().includes(filterValue.toLowerCase()) ||
          order.customer_name?.toLowerCase().includes(filterValue.toLowerCase()) ||
          order.customer_id?.toLowerCase().includes(filterValue.toLowerCase())
      );
    }

    if (statusFilter !== "all" && Array.from(statusFilter).length !== statusOptions.length) {
      filtered = filtered.filter((order) =>
        Array.from(statusFilter).includes(order.status)
      );
    }

    if (clientListFilter) {
      filtered = filtered.filter((order) => (order.customer_name || "N/A") === clientListFilter);
    }

    if (entityListFilter !== "all" && Array.from(entityListFilter).length !== entityList.length) {
      filtered = filtered.filter((order) =>
        Array.from(entityListFilter).includes(order.entity_name || "N/A")
      );
    }

    if (assignmentFilter !== "all") {
      filtered = filtered.filter((order) =>
        Array.from(assignmentFilter).includes(order.assignment_id || "unassigned")
      );
    }

    return filtered;
  }, [
    orders,
    filterValue,
    statusFilter,
    statusOptions.length,
    clientListFilter,
    entityListFilter,
    entityList.length,
    assignmentFilter,
    hasSearchFilter,
  ]);

  return {
    // Search
    filterValue,
    setFilterValue,
    onSearchChange,
    hasSearchFilter,
    // Status filter
    statusFilter,
    setStatusFilter,
    // Client filter
    clientList,
    clientListFilter,
    setClientListFilter,
    // Entity filter
    entityList,
    entityListFilter,
    setEntityListFilter,
    // Assignment filter
    assignmentFilter,
    setAssignmentFilter,
    // Period filter
    periodFilterInterval,
    setPeriodFilterInterval,
    handleRadioChange,
    // Columns
    visibleColumns,
    setVisibleColumns,
    // Pagination
    rowsPerPage,
    setRowsPerPage,
    onRowsPerPageChange,
    page,
    setPage,
    // Sort
    sortDescriptor,
    setSortDescriptor,
    // Filtered items
    filteredItems,
  };
};

export default useOrdersFilters;
